import type { ReportingTreeResponse, ExternalContactRow, UserDirectoryRow } from "@/lib/contactsDirectoryClient";
import { postContactsDirectoryOp } from "@/lib/contactsDirectoryClient";
import { resolveUserManagerProfileFromExternalNode } from "./adminContactsReportingUtils";

type Toast = (msg: string, variant?: "ok" | "err") => void;
type Node = NonNullable<ReportingTreeResponse["nodes"]>[number];

type Params = {
  organizationId: string;
  subordinateManagerKey: string;
  selectedSubordinateKeys: string[];
  reportingTree: ReportingTreeResponse | null;
  externalDirectoryById: Map<string, ExternalContactRow>;
  orgProfileIdByEmail: Map<string, string>;
  reportingNodeByKey: Map<string, Node>;
  normalizeEmail: (v: string | null | undefined) => string;
  external: ExternalContactRow[];
  userDir: UserDirectoryRow[];
  reportingPickerLabel: (n: Node) => string;
  formatUnknownError: (e: unknown) => string;
  toast: Toast;
  setAssigningSubordinates: (v: boolean) => void;
  setLastSubordinateAssignment: (v: { managerLabel: string; subordinateLabels: string[]; assignedAt: number } | null) => void;
  setSelectedSubordinateKeys: (v: string[]) => void;
  load: () => Promise<void>;
  refreshReportingTree: (showSuccessToast?: boolean) => Promise<void>;
};

export function useAdminContactsSubordinateAssignment(p: Params) {
  async function assignSelectedSubordinates() {
    if (!p.subordinateManagerKey) return p.toast("Pick a manager first.", "err");
    if (!p.selectedSubordinateKeys.length) return p.toast("Pick at least one subordinate.", "err");
    const managerNode = (p.reportingTree?.nodes ?? []).find((n) => n.key === p.subordinateManagerKey);
    if (!managerNode) return p.toast("Invalid manager selection.", "err");
    const managerSource = managerNode.source;
    let managerProfileId = managerSource === "user" ? managerNode.profile_id : null;
    const managerExternalRowId = managerSource === "external" ? managerNode.row_id : null;
    if (managerSource === "user" && !managerProfileId) return p.toast("Selected user manager is missing a profile id.", "err");
    if (managerSource === "external" && !managerExternalRowId) return p.toast("Selected external manager is missing a row id.", "err");
    if (managerSource === "external" && managerExternalRowId) {
      const extManager = p.externalDirectoryById.get(managerExternalRowId);
      const extManagerEmail = p.normalizeEmail(extManager?.email);
      managerProfileId =
        managerNode.reports_to_profile_id ||
        (extManagerEmail ? p.orgProfileIdByEmail.get(extManagerEmail) || null : null) ||
        resolveUserManagerProfileFromExternalNode(managerNode, p.externalDirectoryById as any, p.orgProfileIdByEmail, p.reportingNodeByKey, p.normalizeEmail);
    }

    p.setAssigningSubordinates(true);
    try {
      const nodeLabelByKey = new Map((p.reportingTree?.nodes ?? []).map((n) => [n.key, p.reportingPickerLabel(n)]));
      const assignedKeys: string[] = [];
      const failed: Array<{ key: string; reason: string }> = [];
      for (const key of p.selectedSubordinateKeys) {
        const [source, rowId] = key.split(":");
        if (!source || !rowId || key === p.subordinateManagerKey) continue;
        try {
          if (source === "user") {
            const row = p.userDir.find((u) => u.id === rowId);
            if (!row) { failed.push({ key, reason: "User row not found." }); continue; }
            if (!managerProfileId && !managerExternalRowId) { failed.push({ key, reason: "Select a valid manager before assigning subordinates." }); continue; }
            const rowsForProfile = p.userDir.filter((u) => u.profile_id === row.profile_id);
            for (const userRow of rowsForProfile) {
              await postContactsDirectoryOp(p.organizationId, {
                op: "user_row_upsert", id: userRow.id, profile_id: userRow.profile_id, location_id: userRow.location_id || null,
                scope: userRow.scope || "org", department: userRow.department || null, contact_path: userRow.contact_path || null,
                visibility_level: Number(userRow.visibility_level) || 1, max_downstream_visibility_level: userRow.max_downstream_visibility_level ?? null,
                topics: Array.isArray(userRow.topics) ? userRow.topics : [], is_active: userRow.is_active !== false,
                affiliation: String(userRow.affiliation || "org_affiliated"), hierarchy_participation: String(userRow.hierarchy_participation || "full"),
                reports_to_profile_id: managerSource === "user" ? managerProfileId : null, reports_to_external_id: managerSource === "external" ? managerExternalRowId : null,
                allow_vendor_hierarchy_override: false,
              });
            }
            assignedKeys.push(key);
          } else if (source === "external") {
            const row = p.external.find((e) => e.id === rowId);
            if (!row) { failed.push({ key, reason: "External row not found." }); continue; }
            await postContactsDirectoryOp(p.organizationId, {
              op: "external_upsert", id: row.id, name: row.name, email: row.email || null, phone: row.phone || null, company: row.company || null,
              title: row.title || null, department: row.department || null, contact_path: row.contact_path || null, visibility_level: Number(row.visibility_level) || 1,
              max_downstream_visibility_level: row.max_downstream_visibility_level ?? null, location_id: row.location_id || null, scope: row.scope || "org",
              topics: Array.isArray(row.topics) ? row.topics : [], is_active: row.is_active !== false, affiliation: String(row.affiliation || "org_affiliated"),
              hierarchy_participation: String(row.hierarchy_participation || "full"), reports_to_external_id: managerSource === "external" ? managerExternalRowId : null,
              reports_to_profile_id: managerSource === "user" ? managerProfileId : null, allow_vendor_hierarchy_override: false, allow_vendor_reporting_line: false,
            });
            assignedKeys.push(key);
          }
        } catch (err: unknown) { failed.push({ key, reason: p.formatUnknownError(err) }); }
      }
      if (!assignedKeys.length) {
        const first = failed[0];
        return p.toast(first ? `No subordinates were assigned. ${nodeLabelByKey.get(first.key) || first.key}: ${first.reason}` : "No subordinates were assigned. Pick rows and try again.", "err");
      }
      const managerLabel = nodeLabelByKey.get(p.subordinateManagerKey) || p.subordinateManagerKey;
      const subordinateLabels = assignedKeys.map((key) => nodeLabelByKey.get(key) || key);
      p.toast(`Assigned ${assignedKeys.length} subordinate${assignedKeys.length === 1 ? "" : "s"}.`, "ok");
      if (failed.length) {
        const first = failed[0];
        p.toast(`${failed.length} assignment${failed.length === 1 ? "" : "s"} skipped. ${nodeLabelByKey.get(first.key) || first.key}: ${first.reason}`, "err");
      }
      p.setLastSubordinateAssignment({ managerLabel, subordinateLabels, assignedAt: Date.now() });
      p.setSelectedSubordinateKeys([]);
      await p.load();
      await p.refreshReportingTree(false);
    } catch (e: unknown) {
      p.toast(p.formatUnknownError(e), "err");
    } finally {
      p.setAssigningSubordinates(false);
    }
  }

  return { assignSelectedSubordinates };
}
